import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Picker, TextArea } from 'antd-mobile';
import { Input, Form, DatePicker, TimePicker } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DownOutline } from 'antd-mobile-icons';
import SubmitConfirmation from 'utility/SubmitConfirmation';
import { WebviewHttpHandler } from 'shared/webviewHttpHandler';
import FailModal from 'utility/FailModal';
import { cityAreas } from 'shared/cityAreas';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import secureStorage from 'shared/secureStorage';

dayjs.locale('zh-tw');

const webviewHttpHandler = new WebviewHttpHandler(true);
const currentYear = new Date().getFullYear();

const generateYears = (start, end) => {
  const years = [];
  for (let year = start; year <= end; year++) {
    years.push({ label: `${year}年`, value: `${year}年` });
  }
  return years;
};

const generateMonths = () => {
  return Array.from({ length: 12 }, (_, i) => ({
    label: `${i + 1}月`,
    value: `${i + 1}月`,
  }));
};

const generateDays = () => {
  return Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}日`,
    value: `${i + 1}日`,
  }));
};

const generateHalfHourTimes = () => {
  const times = [];
  for (let hour = 9; hour <= 17; hour++) {
    // 示例中从9点开始到17点结束，根据需要调整
    times.push({ label: `${hour}:00`, value: `${hour}:00` });
    if (hour < 17) {
      // 不为17点时添加半小时
      times.push({ label: `${hour}:30`, value: `${hour}:30` });
    }
  }
  return times;
};

const basicColumns = [
  generateYears(currentYear, currentYear + 4),
  generateMonths(),
  generateDays(),
  generateHalfHourTimes(),
];

const AddressSelector = (props) => {
  const { form } = props;
  const [cityPickerVisible, setCityPickerVisible] = useState(false);
  const [areaPickerVisible, setAreaPickerVisible] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  // 当城市变化时更新表单值并设置区域
  const onCityConfirm = (value) => {
    const city = cityAreas.find((c) => c.value === value[0]);
    if (city) {
      setSelectedCity(city.label);
      form.setFieldValue('city', city.label);
      form.setFieldValue('area', ''); // 清除区域值
      setSelectedArea('');
    }
    setCityPickerVisible(false);
  };

  // 当区域变化时更新表单值
  const onAreaConfirm = (value) => {
    const area = cityAreas
      .find((c) => c.label === selectedCity)
      ?.areas.find((a) => a.value === value[0]);
    if (area) {
      setSelectedArea(area.label);
      form.setFieldValue('area', area.label);
    }
    setAreaPickerVisible(false);
  };

  return (
    <Form.Item
      label={<div className="text-body font-normal">取車地點</div>}
      rules={[{ required: true, message: '' }]}
      name="city">
      <div className="space-between mb-4">
        <Form.Item
          name="city"
          rules={[{ required: true, message: '請選擇城市' }]}
          noStyle>
          <Input
            readOnly
            className="large-input"
            placeholder="請選擇城市"
            value={selectedCity}
            onClick={() => setCityPickerVisible(true)}
            suffix={<DownOutline style={{ fontSize: '16px' }} />}
          />
          <Picker
            cancelText="取消"
            confirmText="確定"
            visible={cityPickerVisible}
            columns={[
              cityAreas.map((city) => ({
                label: city.label,
                value: city.value,
              })),
            ]}
            onConfirm={onCityConfirm}
            onClose={() => setCityPickerVisible(false)}
          />
        </Form.Item>
        <Form.Item
          name="area"
          rules={[{ required: true, message: '請選擇區域' }]}
          noStyle>
          <Input
            readOnly
            className="large-input"
            placeholder="請選擇區域"
            value={selectedArea}
            onClick={() => setAreaPickerVisible(true)}
            suffix={<DownOutline style={{ fontSize: '16px' }} />}
            disabled={!selectedCity} // 禁用区域输入直到城市被选中
          />
          <Picker
            cancelText="取消"
            confirmText="確定"
            visible={areaPickerVisible}
            columns={[
              selectedCity
                ? cityAreas
                    .find((c) => c.label === selectedCity)
                    ?.areas.map((area) => ({
                      label: area.label,
                      value: area.value,
                    }))
                : [],
            ]}
            onConfirm={onAreaConfirm}
            onClose={() => setAreaPickerVisible(false)}
          />
        </Form.Item>
      </div>
      <Form.Item
        name="address"
        noStyle
        rules={[{ required: true, message: '請輸入地址' }]}>
        <Input className="large-input" placeholder="請輸入地址" />
      </Form.Item>
    </Form.Item>
  );
};
// ----------------------------------------------------------------

const Inspection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { plateNumber, isAllowInsp } = location.state;
  let now = new Date();

  const [form] = useForm();
  const [loading, setLoading] = useState(false); // 控制提交按钮的loading状态
  // 點擊SubmitConfirmation會出現的Modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  // 提交預約表單失敗Modal
  const [isFailedModalVisible, setIsFailedModalVisible] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const navigateToAppointment = () => {
    navigate('/m/car/CarInfo/Appointment', {
      state: location.state,
    });
  };

  const dateFormatter = (date) => {
    return dayjs(date).format('YYYY/MM/DD(dd)');
  };

  // 檢查星期幾 (保養只能週二～週四)
  const checkDateWeekday = (date) => {
    const weekdayList = [2, 3, 4];
    const day = date.getDay();
    return weekdayList.includes(day);
  };

  // 只能選擇今天之後的日期，及週二～週四
  const disabledDate = (current) => {
    return (
      ![2, 3, 4, 5].includes(current.day()) || current < dayjs().startOf('day')
    );
  };

  // 只能選擇9:00~15:00的時段
  const disabledHours = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      if (i < 9 || i > 15) {
        hours.push(i);
      }
    }
    return hours;
  };

  // handle_submit函数用于处理表单提交
  const handle_submit = (values) => {
    setLoading(true);
    // 提交表单时，将表单数据整理成需要的格式
    const reformatTime1 = values.date1
      ? values.date1
          .hour(values.time1.hour())
          .minute(values.time1.minute())
          .format('YYYY/MM/DD HH:mm')
      : '';
    const reformatTime2 = values.date2
      ? values.date2
          .hour(values.time2.hour())
          .minute(values.time2.minute())
          .format('YYYY/MM/DD HH:mm')
      : '';
    const reformatTime3 = values.date3
      ? values.date3
          .hour(values.time3.hour())
          .minute(values.time3.minute())
          .format('YYYY/MM/DD HH:mm')
      : '';

    const postData = {
      line: secureStorage.getItem('lineUserId'),
      car: plateNumber,
      category: 1,
      appointments: [reformatTime1, reformatTime2, reformatTime3],
      city: values.city,
      district: values.area,
      address: values.address,
      memo: values.memo ? values.memo : '',
    };

    // 提交数据
    webviewHttpHandler
      .post('/service/member/appointment', postData)
      .then((res) => {
        setLoading(false);
        if (!res.success) {
          setErrMsg(res.message);
          setIsFailedModalVisible(true);
        } else {
          setIsModalVisible(true);
        }
      });
  };

  const handleValuesChange = (_, allValues) => {
    form.validateFields();
  };

  useEffect(() => {
    if (!isAllowInsp) {
      window.antdNotify(
        '無法預約',
        '期限內已有驗車保養紀錄，無法再進行預約，如有疑問請洽詢服務專員。',
        'fail'
      );
    }
  }, [isAllowInsp]);

  if (!isAllowInsp) {
    return <></>; // 或者你可以返回一個空的元素，例如 <div></div>
  } else {
    return (
      <>
        {/* 若檢核成功跳出點擊SubmitConfirmation會出現的Modal */}
        <SubmitConfirmation
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          okFunction={navigateToAppointment}
          typeName="驗車"
        />
        <FailModal
          isVisible={isFailedModalVisible}
          setIsVisible={setIsFailedModalVisible}
          title="預約失敗"
          message={errMsg}></FailModal>
        <div className="bill-container">
          <div className="heading font-bold ml-3">驗車預約</div>
          <div className="appointment-container">
            <Form
              className="form-a"
              layout="vertical"
              form={form}
              onFinish={handle_submit}
              onValuesChange={handleValuesChange}
              scrollToFirstError={true}>
              <Form.Item>
                <p className="text-body font-normal">
                  驗車營業時間為
                  <span className="red">每週二、三、四 09:00~15:00</span>及
                  <span className="red">每週五 09:00~12:00</span>
                  ，不包含國定假日。 請至少提供
                  <span className="red">1個</span>
                  可預約的時間，服務管家將會為您安排並且與您聯絡。
                </p>
                <hr></hr>
              </Form.Item>

              {/* 时段一 */}
              <Form.Item
                label={<div className="text-body font-normal">第一時段</div>}>
                <Form.Item
                  name="date1"
                  validateFirst={true}
                  rules={[{ required: true, message: '此欄位必填' }]}>
                  <DatePicker
                    allowClear={false}
                    size={'large'}
                    format={dateFormatter}
                    disabledDate={disabledDate}
                    showNow={false}
                  />
                </Form.Item>
                <Form.Item
                  name="time1"
                  validateFirst={true}
                  rules={[
                    { required: true, message: '此欄位必填' },
                    {
                      validator: (_, value) => {
                        const date1 = form.getFieldValue('date1');
                        if (date1) {
                          let datetime = date1
                            .hour(value.hour())
                            .minute(value.minute());
                          if (datetime < now) {
                            return Promise.reject(
                              new Error('預約時間不得小於當前時間')
                            );
                          } else if (
                            datetime.day() == 5 &&
                            (datetime.hour() > 12 ||
                              (datetime.hour() == 12 && datetime.minute() > 0))
                          ) {
                            return Promise.reject(
                              new Error('週五預約時間不得超過12:00')
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <TimePicker
                    needConfirm={false}
                    changeOnScroll={true}
                    size={'large'}
                    allowClear={false}
                    showNow={false}
                    minuteStep={30}
                    format={'HH:mm'}
                    hideDisabledOptions={true}
                    disabledTime={(current) => {
                      return {
                        disabledHours: () => disabledHours(),
                        disabledMinutes: () => [],
                        disabledSeconds: () => [],
                      };
                    }}
                  />
                </Form.Item>
              </Form.Item>

              {/* 时段二 */}
              <Form.Item
                label={<div className="text-body font-normal">第二時段</div>}>
                <Form.Item
                  name="date2"
                  validateFirst={true}
                  rules={[
                    {
                      validator: (_, value) => {
                        const time2 = form.getFieldValue('time2');
                        if (!value && time2) {
                          return Promise.reject(new Error('請選擇日期。'));
                        } else {
                          return Promise.resolve();
                        }
                      },
                    },
                  ]}>
                  <DatePicker
                    allowClear={true}
                    size={'large'}
                    format={dateFormatter}
                    disabledDate={disabledDate}
                    showNow={false}
                  />
                </Form.Item>
                <Form.Item
                  name="time2"
                  validateFirst={true}
                  rules={[
                    {
                      validator: (_, value) => {
                        const date2 = form.getFieldValue('date2');
                        if (value && date2) {
                          let datetime = date2
                            .hour(value.hour())
                            .minute(value.minute());
                          if (datetime < now) {
                            return Promise.reject(
                              new Error('預約時間不得小於當前時間')
                            );
                          } else if (
                            datetime.day() == 5 &&
                            (datetime.hour() > 12 ||
                              (datetime.hour() == 12 && datetime.minute() > 0))
                          ) {
                            return Promise.reject(
                              new Error('週五預約時間不得超過12:00')
                            );
                          }
                        } else if (!value && date2) {
                          return Promise.reject(new Error('請選擇時間。'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <TimePicker
                    needConfirm={false}
                    changeOnScroll={true}
                    size={'large'}
                    allowClear={true}
                    showNow={false}
                    minuteStep={30}
                    format={'HH:mm'}
                    hideDisabledOptions={true}
                    disabledTime={(current) => {
                      return {
                        disabledHours: () => disabledHours(),
                        disabledMinutes: () => [],
                        disabledSeconds: () => [],
                      };
                    }}
                  />
                </Form.Item>
              </Form.Item>

              {/* 时段三 */}
              <Form.Item
                label={<div className="text-body font-normal">第三時段</div>}>
                <Form.Item
                  name="date3"
                  validateFirst={true}
                  rules={[
                    {
                      validator: (_, value) => {
                        const time3 = form.getFieldValue('time3');
                        if (!value && time3) {
                          return Promise.reject(new Error('請選擇日期。'));
                        } else {
                          return Promise.resolve();
                        }
                      },
                    },
                  ]}>
                  <DatePicker
                    allowClear={true}
                    size={'large'}
                    format={dateFormatter}
                    disabledDate={disabledDate}
                    showNow={false}
                  />
                </Form.Item>
                <Form.Item
                  name="time3"
                  validateFirst={true}
                  rules={[
                    {
                      validator: (_, value) => {
                        const date3 = form.getFieldValue('date3');
                        if (value && date3) {
                          let datetime = date3
                            .hour(value.hour())
                            .minute(value.minute());
                          if (datetime < now) {
                            return Promise.reject(
                              new Error('預約時間不得小於當前時間')
                            );
                          } else if (
                            datetime.day() == 5 &&
                            (datetime.hour() > 12 ||
                              (datetime.hour() == 12 && datetime.minute() > 0))
                          ) {
                            return Promise.reject(
                              new Error('週五預約時間不得超過12:00')
                            );
                          }
                        } else if (!value && date3) {
                          return Promise.reject(new Error('請選擇時間。'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <TimePicker
                    needConfirm={false}
                    changeOnScroll={true}
                    size={'large'}
                    allowClear={true}
                    showNow={false}
                    minuteStep={30}
                    format={'HH:mm'}
                    hideDisabledOptions={true}
                    disabledTime={(current) => {
                      return {
                        disabledHours: () => disabledHours(),
                        disabledMinutes: () => [],
                        disabledSeconds: () => [],
                      };
                    }}
                  />
                </Form.Item>
              </Form.Item>

              {/* 地址 */}
              <AddressSelector form={form} />
              {/* 備註 */}
              <Form.Item
                label={<div className="text-body font-normal">備註</div>}
                name={'memo'}>
                <TextArea
                  style={{
                    border: '1px solid #d8dadd',
                    padding: '8px 16px',
                    borderRadius: '8px',
                  }} // 设置外边框
                  rows={4}
                  placeholder="請輸入"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="submit"
                  className="primary-button"
                  block
                  loading={loading}>
                  提交
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </>
    );
  }
};

export default Inspection;
